import {
  ANAMNESIS_LANGFUSE_TRACE_STORAGE_PREFIX,
  SYNAP_LANGFUSE_DOCTOR_HEADER,
  SYNAP_LANGFUSE_PARENT_SPAN_HEADER,
  SYNAP_LANGFUSE_SESSION_HEADER,
  SYNAP_LANGFUSE_TRACE_HEADER,
  SYNAP_CHUNK_ID_HEADER,
} from './telemetry-metadata'

const createTraceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `synap-trace-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export const getConsultationTraceStorageKey = (consultationId: string): string =>
  `${ANAMNESIS_LANGFUSE_TRACE_STORAGE_PREFIX}${consultationId}`

export const getStoredConsultationTraceId = (consultationId: string): string | null => {
  if (typeof sessionStorage === 'undefined' || !consultationId.trim()) return null
  try {
    return sessionStorage.getItem(getConsultationTraceStorageKey(consultationId))
  } catch {
    return null
  }
}

export const ensureConsultationTraceId = (consultationId: string): string => {
  const existing = getStoredConsultationTraceId(consultationId)
  if (existing) return existing
  const traceId = createTraceId()
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(getConsultationTraceStorageKey(consultationId), traceId)
    } catch {
      /* quota / modo privado */
    }
  }
  return traceId
}

export const clearConsultationTraceId = (consultationId: string): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(getConsultationTraceStorageKey(consultationId))
  } catch {
    /* ignore */
  }
}

export type LangfusePropagationHeaders = Record<string, string>

export const buildLangfusePropagationHeaders = (options: {
  consultationId: string
  traceId?: string | null
  parentSpanId?: string | null
  doctorId?: string | null
}): LangfusePropagationHeaders => {
  const traceId = options.traceId ?? ensureConsultationTraceId(options.consultationId)
  const headers: LangfusePropagationHeaders = {
    [SYNAP_LANGFUSE_TRACE_HEADER]: traceId,
    [SYNAP_LANGFUSE_SESSION_HEADER]: options.consultationId,
  }
  if (options.parentSpanId?.trim()) {
    headers[SYNAP_LANGFUSE_PARENT_SPAN_HEADER] = options.parentSpanId.trim()
  }
  if (options.doctorId?.trim()) {
    headers[SYNAP_LANGFUSE_DOCTOR_HEADER] = options.doctorId.trim()
  }
  return headers
}

export type DistributedTracingHeaders = LangfusePropagationHeaders & Record<string, string>

/**
 * Langfuse (x-synap-langfuse-*) + Sentry (sentry-trace, baggage) para chamadas browser → Edge.
 * `sentryTraceHeaders` vem de `getSentryTracePropagationHeaders()` quando o cliente Sentry está ativo.
 */
export const buildDistributedTracingHeaders = (options: {
  consultationId: string
  traceId?: string | null
  parentSpanId?: string | null
  doctorId?: string | null
  chunkId?: string | null
  sentryTraceHeaders?: Record<string, string> | null
}): DistributedTracingHeaders => {
  const headers: DistributedTracingHeaders = {
    ...buildLangfusePropagationHeaders(options),
  }
  if (options.chunkId?.trim()) {
    headers[SYNAP_CHUNK_ID_HEADER] = options.chunkId.trim()
  }
  if (options.sentryTraceHeaders) {
    for (const [key, value] of Object.entries(options.sentryTraceHeaders)) {
      if (value?.trim()) headers[key] = value.trim()
    }
  }
  return headers
}
